import { z } from 'zod';
import { isoDateTime, mediumText, shortText, uuid } from './primitives.js';
/**
 * Reference documents: a per-session library of files and links the trainer
 * organises into folders. Files are download-only — nothing here is previewed
 * or executed — so the contract only needs to say what a file is well enough
 * to pick its icon.
 */
/** Folders nest at most this deep: a top-level folder is depth 1. */
export const RESOURCE_FOLDER_MAX_DEPTH = 3;
/** What the console and the learner page draw an icon for. */
export const resourceFileType = z.enum([
    'pdf',
    'word',
    'excel',
    'powerpoint',
    'csv',
    'text',
    'markdown',
    'json',
    'notebook',
    'image',
    'archive',
    'other',
]);
const EXTENSION_TYPES = {
    pdf: 'pdf',
    doc: 'word',
    docx: 'word',
    odt: 'word',
    xls: 'excel',
    xlsx: 'excel',
    ods: 'excel',
    ppt: 'powerpoint',
    pptx: 'powerpoint',
    odp: 'powerpoint',
    csv: 'csv',
    tsv: 'csv',
    txt: 'text',
    log: 'text',
    md: 'markdown',
    markdown: 'markdown',
    json: 'json',
    ipynb: 'notebook',
    png: 'image',
    jpg: 'image',
    jpeg: 'image',
    gif: 'image',
    webp: 'image',
    svg: 'image',
    zip: 'archive',
};
/** The icon family for a file name, from its extension. */
export function resourceFileTypeFor(fileName) {
    const dot = fileName.lastIndexOf('.');
    const ext = dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : '';
    return EXTENSION_TYPES[ext] ?? 'other';
}
/**
 * A folder name a learner will read in a breadcrumb. Slashes are refused
 * rather than escaped, so a name never looks like a path it is not.
 */
export const resourceFolderName = z
    .string()
    .trim()
    .min(1, 'Give the folder a name.')
    .max(120, 'Keep folder names under 120 characters.')
    .regex(/^[^/\\\p{Cc}]+$/u, 'Folder names cannot contain slashes or control characters.');
export const sessionResourceFolder = z.object({
    id: uuid,
    parentId: uuid.nullable(),
    name: z.string(),
    depth: z.number().int(),
    orderIndex: z.number().int(),
});
export const sessionResource = z.object({
    id: uuid,
    folderId: uuid.nullable(),
    kind: z.enum(['file', 'link']),
    title: z.string(),
    description: z.string(),
    fileName: z.string().nullable(),
    fileSize: z.number().nullable(),
    /** Icon family. `other` for links and for anything unrecognised. */
    fileType: resourceFileType,
    url: z.string().nullable(),
    orderIndex: z.number().int(),
    updatedAt: isoDateTime,
});
/** The whole library of one session, flat; the client builds the tree. */
export const sessionResourceLibrary = z.object({
    folders: z.array(sessionResourceFolder),
    resources: z.array(sessionResource),
});
/** POST /api/admin/sessions/:sessionId/reference-folders */
export const createResourceFolderRequest = z.object({
    name: resourceFolderName,
    parentId: uuid.nullable().default(null),
});
/** PATCH /api/admin/reference-folders/:folderId — rename, move, or both. */
export const updateResourceFolderRequest = z
    .object({
    name: resourceFolderName.optional(),
    /** `null` moves the folder to the top level. */
    parentId: uuid.nullable().optional(),
})
    .refine((v) => v.name !== undefined || v.parentId !== undefined, {
    message: 'Send a new name, a new parent, or both.',
});
/** POST /api/admin/sessions/:sessionId/reference-docs/links */
export const createResourceLinkRequest = z.object({
    title: shortText.trim().min(1),
    description: mediumText.default(''),
    url: z
        .string()
        .url()
        .max(2_000)
        .refine((u) => /^https?:\/\//i.test(u), 'Links must start with http:// or https://.'),
    folderId: uuid.nullable().default(null),
});
/** PATCH /api/admin/reference-docs/:resourceId — edit the label, or move it. */
export const updateResourceRequest = z
    .object({
    title: shortText.trim().min(1).optional(),
    description: mediumText.optional(),
    url: z
        .string()
        .url()
        .max(2_000)
        .refine((u) => /^https?:\/\//i.test(u), 'Links must start with http:// or https://.')
        .optional(),
    /** `null` moves it to the top level. */
    folderId: uuid.nullable().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Nothing to change.',
});
/**
 * POST /api/admin/sessions/:sessionId/reference-docs/reorder — the new order of
 * what sits directly inside one folder (or the top level). Folders and files
 * are ordered separately: folders always list first.
 */
export const reorderResourcesRequest = z.object({
    folderId: uuid.nullable(),
    kind: z.enum(['folders', 'resources']),
    order: z.array(uuid).min(1),
});
/** GET /api/reference-docs/:resourceId/download — a fresh, short-lived link. */
export const resourceDownloadResponse = z.object({
    url: z.string(),
    expiresInSec: z.number().int(),
});
//# sourceMappingURL=resources.js.map